import type { RuteroBookingTax } from '@bandido/shared/types';

import type { RuteroMoney } from './queries';
import type { OfferOptionsSelection } from './queries';
import { offerSelectionHasChoices } from './offerOptionsPreview';

/**
 * El precio del momento, en UNA sola expresión.
 *
 * El modal tiene dos fuentes de precio y una sola puede mandar por momento:
 * antes de reservar, el total cotizado de la oferta (que el proveedor puede
 * declarar INDICATIVO); con la reserva creada, el snapshot comercial
 * CONGELADO de la fila. Que las dos convivan en el mismo render es cómo se le
 * hace confirmar a un asesor un precio que ya no existe, así que de acá sale
 * todo lo que la barra pinta: el importe, su leyenda, el número del proveedor
 * y el aviso de reprecio.
 *
 * Nada de esto se CALCULA: no hay restas, ni markup, ni conversiones — eso es
 * del backend (`formatMoney` es lo único monetario del frontend). Lo único
 * que se hace acá es COMPARAR dos importes que el backend ya emitió, para
 * decidir si hay algo que avisar; la diferencia entre ellos no se computa ni
 * se muestra: se muestran los dos importes, tal como llegaron.
 */
export interface BookingPriceInput {
  /**
   * La autoridad del momento, ya resuelta
   * (`resolveBookingModalDisplayAuthority`): quién manda —la oferta o la
   * reserva— y con qué importe. No se vuelve a decidir acá.
   */
  authority: { isPostHold: boolean; commercialMoney?: RuteroMoney };
  /** La oferta que se está por reservar. `null` con la reserva creada. */
  offer: { priceIsIndicative?: boolean } | null;
  /** La reserva creada, con su snapshot comercial congelado. */
  booking: {
    quotedAmountMinor?: number;
    quotedTaxes?: RuteroBookingTax[];
    currency?: string;
  } | null;
  /**
   * El importe que la barra tenía DELANTE del asesor al apretar Reservar. Se
   * captura antes de la mutación (como el resumen del viaje) porque es lo
   * único contra lo que se puede contrastar el congelado: es la cifra con la
   * que se tomó la decisión. Ausente cuando el modal se abre sobre una reserva
   * que ya existía — ahí no hay nada que contradecir y no se avisa nada.
   */
  shownBeforeHold?: RuteroMoney;
  /**
   * Para quién se dibuja (`useAudience`). La audiencia decide cuánto se
   * revela de la COMPOSICIÓN del precio, nunca el precio: el número del
   * proveedor y su desglose son del asesor —con el total al lado dejan leer
   * el margen de la agencia, igual que el neto— y para una audiencia
   * minorista quedan afuera. El total y el reprecio (dos precios de venta) se
   * muestran siempre: es lo que el cliente paga.
   */
  isAgent: boolean;
}

export interface BookingPriceView {
  /** El importe que encabeza la barra en este momento. */
  total?: RuteroMoney;
  /**
   * El total puede no ser el final: la oferta viene marcada como indicativa
   * (`priceIsIndicative`), así que el precio de verdad llega al reservar.
   * Solo antes del hold: con la reserva creada el precio ya es firme.
   */
  estimated: boolean;
  /**
   * El precio del PROVEEDOR congelado al reservar. Solo post-hold, solo si la
   * fila lo trae (reservas sin snapshot congelado no lo tienen) y solo para
   * el asesor: con el total al lado revela el margen.
   */
  quoted?: RuteroMoney;
  /**
   * El congelado del proveedor ES el total: la leyenda describe el importe de
   * arriba y no hace falta repetir el número. En falso (con `quoted`
   * presente) el número del proveedor se muestra aparte, porque el total
   * lleva el markup de la agencia y son dos cifras distintas.
   */
  quotedIsTotal: boolean;
  /**
   * Los renglones con los que el proveedor explica `quoted`. Vacío = no
   * publicó desglose (se muestra el precio sin él, nunca uno inventado) o la
   * audiencia no ve la composición del precio. Nunca se suman: el total
   * autoritativo es `quoted`.
   */
  quotedTaxes: RuteroBookingTax[];
  /**
   * El proveedor cambió el precio al reservar. Presente SIEMPRE que el
   * congelado difiera de lo que se mostró antes de reservar, sin umbral: una
   * diferencia chica sigue siendo plata del cliente, y esconderla es hacerle
   * confirmar al asesor un precio que ya no existe.
   */
  reprice?: { previous: RuteroMoney; current: RuteroMoney };
}

const sameMoney = (a: RuteroMoney, b: RuteroMoney): boolean =>
  a.amountMinor === b.amountMinor && a.currency === b.currency;

/**
 * Evidencia que habilita a afirmar que el proveedor CAMBIÓ el precio al
 * reservar. Sin opciones elegidas, el total de la oferta es comparable. Con
 * una selección comercial, solo el preview autoritativo de ESA selección lo
 * es: si el preview no estuvo disponible, el número pintado era el precio
 * base y compararlo contra el hold (que ya incluye extras) produciría un falso
 * reprecio.
 */
export function preHoldRepriceComparison({
  displayed,
  selection,
  selectionPriceAuthoritative,
}: {
  displayed?: RuteroMoney;
  selection: OfferOptionsSelection;
  /** Preview vivo o quote de Tours ya congelado para el hold. */
  selectionPriceAuthoritative: boolean;
}): RuteroMoney | undefined {
  if (!displayed) return undefined;
  if (!offerSelectionHasChoices(selection)) return displayed;
  return selectionPriceAuthoritative ? displayed : undefined;
}

/**
 * El precio congelado del proveedor, armado con la moneda de la fila: el
 * backend congela el importe del proveedor y la base comercial a partir del
 * MISMO valor, así que `currency` es la de los dos. Sin importe o sin moneda
 * no hay número que mostrar (reservas anteriores al campo, o sin snapshot
 * congelado todavía).
 */
function frozenQuotedPrice(
  booking: BookingPriceInput['booking'],
): RuteroMoney | undefined {
  if (!booking) return undefined;
  const { quotedAmountMinor, currency } = booking;
  return typeof quotedAmountMinor === 'number' && currency
    ? { amountMinor: quotedAmountMinor, currency }
    : undefined;
}

export function resolveBookingPrice({
  authority,
  offer,
  booking,
  shownBeforeHold,
  isAgent,
}: BookingPriceInput): BookingPriceView {
  const total = authority.commercialMoney;

  if (!authority.isPostHold) {
    return {
      total,
      estimated: offer?.priceIsIndicative === true,
      quotedIsTotal: false,
      quotedTaxes: [],
    };
  }

  const quoted = isAgent ? frozenQuotedPrice(booking) : undefined;
  const taxes = isAgent ? booking?.quotedTaxes : undefined;

  return {
    total,
    // Post-hold el precio es el que congeló el proveedor: no queda nada
    // "estimado" que rotular, aunque la oferta lo fuera.
    estimated: false,
    quoted,
    quotedIsTotal: Boolean(quoted && total && sameMoney(quoted, total)),
    quotedTaxes: Array.isArray(taxes) ? taxes : [],
    ...(shownBeforeHold && total && !sameMoney(shownBeforeHold, total)
      ? { reprice: { previous: shownBeforeHold, current: total } }
      : {}),
  };
}
